/**
 * Блок «Семейное положение» (ТЗ п. 5.2.9, правила R-19…R-24).
 *
 * Статус — МНОЖЕСТВЕННЫЙ выбор: комбинация «состоит в браке» и «брак расторгнут»
 * допустима и даёт в заявлении обе формулировки подряд (правило R-22),
 * а в перечень приложений — оба свидетельства (ТЗ п. 6.4).
 */

import { newRowId, type Child, type MaritalStatus, type Spouse } from '@bd/domain';
import { DataGrid } from '../../../components/DataGrid.js';
import type { BlockProps } from './types.js';

const STATUSES: Array<[MaritalStatus, string]> = [
  ['single', 'Не состоит в браке'],
  ['married', 'Состоит в браке'],
  ['divorced', 'Брак расторгнут'],
];

export function FamilyBlock({ profile, update, readOnly, errorFor }: BlockProps) {
  const family = profile.family;

  const toggleStatus = (status: MaritalStatus) => {
    update((p) => {
      const has = p.family.statuses.includes(status);
      let statuses = has ? p.family.statuses.filter((s) => s !== status) : [...p.family.statuses, status];

      // «Не состоит в браке» несовместимо с остальными статусами
      if (!has && status === 'single') statuses = ['single'];
      if (!has && status !== 'single') statuses = statuses.filter((s) => s !== 'single');

      return { ...p, family: { ...p.family, statuses } };
    });
  };

  const spouses = (relation: Spouse['relation']) => family.spouses.filter((s) => s.relation === relation);

  /**
   * Супруги хранятся одним списком с признаком «нынешний / бывший»,
   * а правятся двумя таблицами. Новый состав считается от АКТУАЛЬНОГО списка,
   * а не от того, что таблица видела при отрисовке: иначе правка в одной
   * таблице затирала бы соседнюю.
   */
  const setSpouses = (relation: Spouse['relation'], apply: (previous: Spouse[]) => Spouse[]) =>
    update((p) => ({
      ...p,
      family: {
        ...p.family,
        spouses: [
          ...p.family.spouses.filter((s) => s.relation !== relation),
          ...apply(p.family.spouses.filter((s) => s.relation === relation)),
        ],
      },
    }));

  return (
    <section className="block">
      <h2>Семья</h2>

      <div className="checkbox-row">
        {STATUSES.map(([status, title]) => (
          <label key={status} className="field field-inline">
            <input
              type="checkbox"
              checked={family.statuses.includes(status)}
              disabled={readOnly}
              onChange={() => toggleStatus(status)}
            />
            <span>{title}</span>
          </label>
        ))}
      </div>
      <p className="hint">
        Можно выбрать одновременно «состоит в браке» и «брак расторгнут»: заявление получит обе формулировки,
        а перечень приложений — оба свидетельства.
      </p>
      {errorFor('family.statuses') && <p className="error-text">{errorFor('family.statuses')}</p>}
      {errorFor('family.spouses') && <p className="error-text">{errorFor('family.spouses')}</p>}

      {family.statuses.includes('married') && (
        <>
          <h3>Действующий брак</h3>
          <DataGrid<Spouse>
            rows={spouses('current')}
            readOnly={readOnly}
            getRowId={(row) => row.id}
            onChange={(apply) => setSpouses('current', apply)}
            createRow={() => ({ id: newRowId(), fio: '', relation: 'current', marriedAt: null, divorcedAt: null })}
            columns={[
              { key: 'fio', title: 'ФИО супруга', width: 60, get: (r) => r.fio, set: (r, v) => ({ ...r, fio: v }) },
              {
                key: 'marriedAt', title: 'Дата заключения брака', width: 40, type: 'date',
                get: (r) => r.marriedAt ?? '',
                set: (r, v) => ({ ...r, marriedAt: v || null }),
              },
            ]}
          />
        </>
      )}

      {family.statuses.includes('divorced') && (
        <>
          <h3>Расторгнутые браки</h3>
          <DataGrid<Spouse>
            rows={spouses('former')}
            readOnly={readOnly}
            getRowId={(row) => row.id}
            onChange={(apply) => setSpouses('former', apply)}
            createRow={() => ({ id: newRowId(), fio: '', relation: 'former', marriedAt: null, divorcedAt: null })}
            emptyText="Добавьте бывшего супруга: без него формулировка о расторгнутом браке не сформируется."
            columns={[
              { key: 'fio', title: 'ФИО бывшего супруга', width: 50, get: (r) => r.fio, set: (r, v) => ({ ...r, fio: v }) },
              {
                key: 'marriedAt', title: 'Дата заключения', width: 25, type: 'date',
                get: (r) => r.marriedAt ?? '',
                set: (r, v) => ({ ...r, marriedAt: v || null }),
              },
              {
                key: 'divorcedAt', title: 'Дата расторжения', width: 25, type: 'date',
                get: (r) => r.divorcedAt ?? '',
                set: (r, v) => ({ ...r, divorcedAt: v || null }),
              },
            ]}
          />
        </>
      )}

      <h3>Дети</h3>
      <DataGrid<Child>
        rows={family.children}
        readOnly={readOnly}
        getRowId={(row) => row.id}
        onChange={(apply) => update((p) => ({ ...p, family: { ...p.family, children: apply(p.family.children) } }))}
        createRow={() => ({ id: newRowId(), fio: '', birthDate: null, dependent: true })}
        emptyText="Детей нет — заявление получит формулировку «на иждивении детей не содержит»."
        columns={[
          { key: 'fio', title: 'ФИО ребёнка', width: 55, get: (r) => r.fio, set: (r, v) => ({ ...r, fio: v }) },
          {
            key: 'birthDate', title: 'Дата рождения', width: 25, type: 'date',
            get: (r) => r.birthDate ?? '',
            set: (r, v) => ({ ...r, birthDate: v || null }),
          },
          {
            key: 'dependent', title: 'На иждивении', width: 20, type: 'checkbox', align: 'center',
            get: (r) => r.dependent,
            set: (r, v) => ({ ...r, dependent: Boolean(v) }),
          },
        ]}
      />
    </section>
  );
}
